import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useCasaneoAuth } from "../context/CasaneoAuthContext";
import { formatError } from "../lib/errors";

/**
 * Landing page for the Emergent Google OAuth redirect for the Casanéo backend.
 *
 * The user reaches `${origin}/casaneo-auth-callback#casaneo_session_id=...`.
 * We synchronously read the fragment, exchange it against `/api/auth/google/session`
 * and navigate back to `/`.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function CasaneoAuthCallback() {
  const navigate = useNavigate();
  const { exchangeGoogleSession } = useCasaneoAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/casaneo_session_id=([^&]+)/);
    if (!match) {
      setError("Aucun session_id détecté");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);
    // Clean the hash so a refresh doesn't retry the exchange.
    window.history.replaceState(null, "", window.location.pathname);
    exchangeGoogleSession(sessionId)
      // Rechargement complet : AuthContext (tableau de bord) relit la session
      // Casanéo au démarrage — sinon la page d'accueil publique s'affiche.
      .then(() => window.location.replace("/"))
      .catch((e) => setError(formatError(e, "Connexion Google impossible")));
  }, [exchangeGoogleSession, navigate]);

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="text-center space-y-3" data-testid="casaneo-auth-callback">
        {error ? (
          <>
            <p className="text-rose-600 font-medium">{error}</p>
            <button
              onClick={() => navigate("/casaneo-login")}
              className="text-brass hover:underline text-sm"
              data-testid="casaneo-callback-retry"
            >
              Réessayer
            </button>
          </>
        ) : (
          <>
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-brass" />
            <p className="text-sm text-muted-foreground">Connexion à Casanéo…</p>
          </>
        )}
      </div>
    </div>
  );
}
